/**
 * Unified Simulation Engine (Plain JavaScript)
 *
 * This engine combines logic from simAhuChillerSetup.mjs and sim_enhanced.js
 * with the following improvements:
 * - Uses Project Haystack tags instead of custom point names
 * - Scenario-driven configuration (equipment, correlations, interactions)
 * - Supports control rules and anomaly injection from scenarios
 * - Maintains cumulative state tracking
 *
 * Usage in scripts:
 *   const { PlatformApi } = libraries;
 *   const { runUnifiedSimulation, loadDisplayRanges } = await import('./simulationEngine.mjs');
 */

// ============================================================================
// CONSTANTS (inline since we can't import)
// ============================================================================

const DISPLAY_POINTS_USER_TYPE = 'telemetry_display_pts';
const TELEMETRY_COLL_USER_TYPE = 'iaf_telemetry_timeseries';
const SIMULATION_SCENARIO_USER_TYPE = 'simulation_scenario';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Decodes MongoDB Decimal128 objects to JavaScript numbers
 */
function decodeDecimal128({ high, low, negative }) {
  const SIGN_BIT_MASK = 0x8000000000000000n;
  const COMBINATION_MASK = 0x7C00000000000000n;

  // Convert to BigInts
  const highBig = BigInt(high);
  const lowBig = BigInt(low);

  // Extract sign
  const isNegative = (highBig & SIGN_BIT_MASK) !== 0n;

  // Extract the combination field (bits 1-5 after sign)
  const comb = (highBig & COMBINATION_MASK) >> 58n;

  // Handle special values
  if (comb === 0x1fn) return NaN;
  if (comb === 0x1en) return isNegative ? -Infinity : Infinity;

  // Extract exponent (bits 1–14 after sign)
  const biasedExponent = Number((highBig >> 49n) & 0x3FFFn);
  const exponent = biasedExponent - 6176; // Decimal128 bias is 6176

  // Extract significand (coefficient)
  const significandHigh = highBig & 0x0001FFFFFFFFFFFFn;
  const significand = (significandHigh << 64n) | lowBig;

  // Convert to decimal
  const decimalValue = BigInt(significand.toString());

  // Convert to float
  const floatVal = Number(decimalValue) * 10 ** exponent;

  return isNegative ? -floatVal : floatVal;
}

/**
 * Check if an object is a Decimal128 object
 */
function isDecimal128(obj) {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return false;
  }
  return (
    'high' in obj &&
    'low' in obj &&
    typeof obj.high === 'number' &&
    typeof obj.low === 'number' &&
    Number.isInteger(obj.high) &&
    Number.isInteger(obj.low) &&
    'negative' in obj &&
    'finite' in obj
  );
}

/**
 * Recursively convert any Decimal128 objects to numbers
 */
function sanitizeDecimal128(obj) {
  if (obj === null || obj === undefined) return obj;

  if (isDecimal128(obj)) {
    return decodeDecimal128(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeDecimal128(item));
  }

  if (typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = sanitizeDecimal128(value);
    }
    return result;
  }

  return obj;
}

const randBetween = (min, max) =>
  +(min + Math.random() * (max - min)).toFixed(2);

const clamp = (v, min, max) =>
  Math.max(min, Math.min(max, v));

function getTimeOfDayLoad(hour) {
  if (hour >= 8 && hour < 18) return 1.0;
  if (hour >= 6 && hour < 8) return 0.6;
  if (hour >= 18 && hour < 22) return 0.7;
  return 0.2;
}

function getMonthLoadFactor(month) {
  const summer = [5, 6, 7]; // June, July, August
  if (summer.includes(month)) return 1.1;
  if (month === 11 || month === 0 || month === 1) return 0.8; // Winter
  return 1.0;
}

function getEnvLoad(date) {
  const h = date.getHours();
  const m = date.getMonth();
  const tod = getTimeOfDayLoad(h);
  const season = getMonthLoadFactor(m);
  let load = tod * season;
  load += randBetween(-0.05, 0.05);
  return clamp(load, 0, 1);
}

function getTimeOfDayFactor(hour) {
  if (hour >= 8 && hour < 18) return 1.15;
  if (hour >= 6 && hour < 8) return 0.9;
  if (hour >= 18 && hour < 22) return 0.95;
  return 0.75;
}

function getOccupancyFactor(hour) {
  if (hour >= 9 && hour < 17) return 1.1;
  if (hour >= 7 && hour < 9) return 0.95;
  if (hour >= 17 && hour < 20) return 0.9;
  return 0.8;
}

// Month equipment factors - simplified version
const MONTH_EQUIPMENT_FACTORS = {};

function getRange(displayRanges, haystackTag, fallback) {
  const r = displayRanges[haystackTag];
  if (!r) return fallback;
  const min = Number(r.normalMin);
  const max = Number(r.normalMax);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
    return fallback;
  }
  return { normalMin: min, normalMax: max };
}

function valueInNormalBand(normalMin, normalMax, loadFactor, noiseFraction = 0.1) {
  const span = normalMax - normalMin;
  const basePos = 0.25 + 0.5 * loadFactor;
  let val = normalMin + span * basePos;
  const noise = span * noiseFraction;
  val += randBetween(-noise, noise);
  return clamp(val, normalMin, normalMax);
}

function maybeOutlier(value, normalMin, normalMax, outlierProbability = 0.05, outlierSpanFactor = 0.25) {
  if (Math.random() >= outlierProbability) return value;

  const span = normalMax - normalMin;
  const extra = span * (0.1 + Math.random() * outlierSpanFactor);
  const goLow = Math.random() < 0.5;
  return goLow ? value - extra : value + extra;
}

// ============================================================================
// DISPLAY RANGES LOADER
// ============================================================================

async function loadDisplayRanges(ctx, IafItemSvc) {
  try {
    const collections = await IafItemSvc.getNamedUserItems(
      { query: { _userType: DISPLAY_POINTS_USER_TYPE, _kind: 'collection' } },
      ctx
    );

    if (!collections._list || collections._list.length === 0) {
      console.warn('No telemetry_display_pts collections found');
      return {};
    }

    const collection = collections._list[0];

    const pointsRes = await IafItemSvc.getRelatedItems(
      collection._userItemId || '',
      {},
      ctx,
      { page: { getAllItems: true } }
    );

    const displayRanges = {};

    for (const row of (pointsRes._list || [])) {
      const haystackTag = row.haystackTag;
      const ranges = (row.ranges || []);

      if (!haystackTag || typeof haystackTag !== 'string' || !ranges.length) continue;

      const normalBands = ranges.filter((r) => r.feeling === 0 || r.feeling === 1);

      if (!normalBands.length) continue;

      const normalMin = Math.min(...normalBands.map((r) => r.from));
      const normalMax = Math.max(...normalBands.map((r) => r.to));

      displayRanges[haystackTag] = { normalMin, normalMax };
    }

    return displayRanges;
  } catch (e) {
    console.error('loadDisplayRanges error:', e);
    return {};
  }
}

// ============================================================================
// DISPLAY POINTS ENRICHMENT
// ============================================================================

async function loadDisplayPointsIndex(ctx, IafItemSvc) {
  try {
    const collections = await IafItemSvc.getNamedUserItems(
      { query: { _userType: DISPLAY_POINTS_USER_TYPE, _kind: 'collection' } },
      ctx
    );
    const coll = collections._list?.[0];
    if (!coll) return {};

    const pointsRes = await IafItemSvc.getRelatedItems(coll._userItemId || '', {}, ctx, {
      page: { getAllItems: true }
    });

    const index = {};
    for (const row of pointsRes._list || []) {
      const dp = row;
      const hay = dp.haystackTag;
      const types = Array.isArray(dp.dtTypes) ? dp.dtTypes : [];
      if (!hay || types.length === 0) continue;
      for (const t of types) {
        const key = `${t}::${hay}`.toLowerCase();
        index[key] = dp;
      }
    }
    return index;
  } catch (e) {
    console.warn('loadDisplayPointsIndex failed:', e);
    return {};
  }
}

function enrichScenarioEquipmentDataPoints(scenario, index, displayRanges) {
  const newEquipment = scenario.equipment.map((eq) => {
    const dtType = (eq.equipmentTypeName || '').toLowerCase();
    const newDps = (eq.dataPoints || []).map((dp) => {
      const hay = dp.haystackTag || dp.haystackTag;
      const key = `${dtType}::${hay}`.toLowerCase();
      const match = index[key];
      const kind = (match?.kind || dp.kind || 'Number').toString();
      const unit = match?.unit || dp.unit || '';

      let min = dp.min;
      let max = dp.max;
      if ((min == null || max == null) && displayRanges[hay]) {
        min = displayRanges[hay].normalMin;
        max = displayRanges[hay].normalMax;
      } else if ((min == null || max == null) && match?.ranges && match.ranges.length) {
        const normals = match.ranges.filter((r) => r.feeling === 0 || r.feeling === 1);
        if (normals.length) {
          min = Math.min(...normals.map((r) => r.from));
          max = Math.max(...normals.map((r) => r.to));
        }
      }
      if (min == null || max == null) {
        min = typeof dp.min === 'number' ? dp.min : 0;
        max = typeof dp.max === 'number' ? dp.max : 100;
      }

      return {
        name: dp.name || match?.pointName || hay,
        haystackTag: hay,
        type: dp.type || (kind === 'Boolean' ? 'boolean' : 'float'),
        unit,
        min,
        max,
        typical: dp.typical,
        average: dp.average,
      };
    });

    return { ...eq, dataPoints: newDps };
  });

  return { ...scenario, equipment: newEquipment };
}

// ============================================================================
// SENSOR CORRELATIONS
// ============================================================================

function applySensorCorrelations(equipment, sensors, prevSensors, configCorrelations) {
  if (!equipment.dataPoints || equipment.dataPoints.length === 0) {
    return sensors;
  }

  const category = equipment.category.toUpperCase();
  const equipType = equipment.equipmentTypeName.toLowerCase();

    const isPairConfigured = (sensorName, targetName) => {
        if (!Array.isArray(configCorrelations)) return false;
        return configCorrelations.some(c =>
            c &&
            (
                (c.sensor === sensorName && Array.isArray(c.affects) && c.affects.includes(targetName)) ||
                (c.mode === 'boolean-rule' && c.target === targetName)
            )
        );
    };

  // Apply configured correlations if provided
  if (Array.isArray(configCorrelations) && configCorrelations.length > 0) {
    for (const correlation of configCorrelations) {

        //BOOLEAN RULE SUPPORT
        if (correlation.mode === 'boolean-rule' && correlation.target && typeof correlation.rule === 'string') {
            try {
                const fn = new Function('sensors', `return (${correlation.rule});`);
                sensors[correlation.target] = !!fn(sensors);
            } catch (err) {
                console.warn('Invalid boolean rule:', correlation.rule, err);
            }
            continue;
        }

        //STANDARD NUMERIC CORRELATIONS

      if (!correlation || !correlation.sensor || !Array.isArray(correlation.affects)) continue;

      const sourceValue = sensors[correlation.sensor];
      if (sourceValue == null) continue;


      if (correlation.mode === 'target-blend') {
        const offset = correlation.offset || 0;
        const blendFactor = correlation.blendFactor != null ? correlation.blendFactor : 0.3;
        const retentionFactor = 1 - blendFactor;
        for (const target of correlation.affects) {
          const currentValue = sensors[target];
          if (currentValue == null) continue;
          const targetValue = sourceValue + offset;
          sensors[target] = +(currentValue * retentionFactor + targetValue * blendFactor).toFixed(2);
        }
      } else if (typeof correlation.factor === 'number') {
        for (const target of correlation.affects) {
          const currentValue = sensors[target];
          const prevSource = prevSensors[correlation.sensor];
          if (currentValue == null || prevSource == null) continue;
          const delta = sourceValue - prevSource;
          sensors[target] = +(currentValue + delta * correlation.factor).toFixed(2);
        }
      }
    }
  }

    // -------------------------------
    // ✅ 2. DEFAULT BOOLEAN PHYSICS (ONLY IF NOT CONFIGURED)
    // -------------------------------

    //Boiler flame status
    if (equipType.includes('boiler') && sensors['flame-proven'] == null && !isPairConfigured('fuel-flow', 'flame-proven')) {
        const fuelFlow = sensors['fuel-flow'];
        if (fuelFlow != null) {
            sensors['flame-proven'] = fuelFlow > 0;
        }
    }

    //Pump running status
    if (equipType.includes('pump') && sensors['pump-status'] == null && !isPairConfigured('water-flow', 'pump-status')) {
        const flow = sensors['water-flow'] ?? sensors['flow-rate'];
        if (flow != null) {
            sensors['pump-status'] = flow > 0.05;
        }
    }

    //Fan / AHU running status
    if ((equipType.includes('fan') || category.includes('AHU')) && sensors['fan-status'] == null) {
        const air = sensors['discharge-air-flow'] ?? sensors['air-flow'];
        if (air != null) {
            sensors['fan-status'] = air > 0.05;
        }
    }

    //Electrical breaker state
    if (sensors['breaker-status'] == null && sensors['current'] != null) {
        sensors['breaker-status'] = sensors['current'] > 0;
    }

    // -------------------------------
    // ✅ DEFAULT NUMERIC LOGIC (ONLY IF NOT CONFIGURED)
    // -------------------------------

  // HVAC/Chiller correlations
  if (category.includes('CHILLER') || category.includes('HVAC')) {
    const lwt = sensors['chilled-water-leaving-temp'];
    const ewt = sensors['chilled-water-entering-temp'];

    if (lwt != null && ewt != null && !isPairConfigured('chilled-water-leaving-temp', 'chilled-water-entering-temp')) {
      const targetEwt = lwt + 10;
      sensors['chilled-water-entering-temp'] = +(ewt * 0.7 + targetEwt * 0.3).toFixed(2);
    }

    const loopEwt = sensors['condenser-water-entering-temp'];
    const loopLwt = sensors['condenser-water-leaving-temp'];

    if (loopEwt != null && loopLwt != null && !isPairConfigured('condenser-water-entering-temp', 'condenser-water-leaving-temp')) {
      const targetLwt = loopEwt + 30;
      sensors['condenser-water-leaving-temp'] = +(loopLwt * 0.6 + targetLwt * 0.4).toFixed(2);
    }

    const flow = sensors['chilled-water-flow'];
    const power = sensors['elec-power'];
    if (flow != null && power != null && prevSensors['chilled-water-flow'] != null && !isPairConfigured('chilled-water-flow', 'elec-power')) {
      const flowDelta = flow - prevSensors['chilled-water-flow'];
      sensors['elec-power'] = +(power + flowDelta * 0.6).toFixed(2);
    }
  }

  // AHU / Air Handling correlations
  if (category.includes('AHU') || category.includes('AIR') || equipType.includes('air handling')) {
    const supplyTemp = sensors['discharge-air-temp'];
    const returnTemp = sensors['return-air-temp'];

    if (supplyTemp != null && returnTemp != null && !isPairConfigured('discharge-air-temp', 'return-air-temp')) {
      const targetRet = supplyTemp - 10;
      sensors['return-air-temp'] = +(returnTemp * 0.7 + targetRet * 0.3).toFixed(2);
    }

    const supplyHumidity = sensors['discharge-air-humidity'];

    if (supplyTemp != null && supplyHumidity != null && !isPairConfigured('discharge-air-temp', 'discharge-air-humidity')) {
      const targetRh = clamp(70 - (supplyTemp - 70) * 0.5, 30, 80);
      sensors['discharge-air-humidity'] = +(supplyHumidity * 0.6 + targetRh * 0.4).toFixed(2);
    }

    const airPressure = sensors['air-pressure'];
    const airflowRate = sensors['discharge-air-flow'];

    if (airPressure != null && airflowRate != null && prevSensors['air-pressure'] != null && !isPairConfigured('air-pressure', 'discharge-air-flow')) {
      const pressureDelta = airPressure - prevSensors['air-pressure'];
      sensors['discharge-air-flow'] = +(airflowRate + pressureDelta * 0.5).toFixed(2);
    }
  }

  // Boiler correlations
  if (equipType.includes('boiler')) {
    const inletTemp = sensors['water-inlet-temp'];
    const outletTemp = sensors['water-outlet-temp'];

    if (inletTemp != null && outletTemp != null && prevSensors['water-inlet-temp'] != null && !isPairConfigured('water-inlet-temp', 'water-outlet-temp')) {
      const inletDelta = inletTemp - prevSensors['water-inlet-temp'];
      sensors['water-outlet-temp'] = +(outletTemp + inletDelta * 0.7).toFixed(2);
    }

    if (outletTemp != null && sensors['steam-pressure'] != null && prevSensors['water-outlet-temp'] != null && !isPairConfigured('water-outlet-temp', 'steam-pressure')) {
      const outletDelta = outletTemp - prevSensors['water-outlet-temp'];
      sensors['steam-pressure'] = +(sensors['steam-pressure'] + outletDelta * 0.8).toFixed(2);
    }

    const fuelFlow = sensors['fuel-flow'];
    if (fuelFlow != null && prevSensors['fuel-flow'] != null) {
      const fuelDelta = fuelFlow - prevSensors['fuel-flow'];
      if (sensors['water-outlet-temp'] != null && !isPairConfigured('fuel-flow', 'water-outlet-temp')) {
        sensors['water-outlet-temp'] = +(sensors['water-outlet-temp'] + fuelDelta * 0.6).toFixed(2);
      }
      if (sensors['steam-pressure'] != null && !isPairConfigured('fuel-flow', 'steam-pressure')) {
        sensors['steam-pressure'] = +(sensors['steam-pressure'] + fuelDelta * 0.6).toFixed(2);
      }
    }
  }

  // Pump correlations
  if (equipType.includes('pump')) {
    const flowRate = sensors['water-flow'] || sensors['flow-rate'];
    const dischargePressure = sensors['discharge-pressure'];

    if (flowRate != null && dischargePressure != null && (prevSensors['water-flow'] != null || prevSensors['flow-rate'] != null)) {
      const prevFlow = prevSensors['water-flow'] || prevSensors['flow-rate'];
      const flowDelta = flowRate - prevFlow;
      sensors['discharge-pressure'] = +(dischargePressure + flowDelta * 0.6).toFixed(2);
    }

    const power = sensors['elec-power'];
    if (flowRate != null && power != null && (prevSensors['water-flow'] != null || prevSensors['flow-rate'] != null)) {
      const prevFlow = prevSensors['water-flow'] || prevSensors['flow-rate'];
      const flowDelta = flowRate - prevFlow;
      sensors['elec-power'] = +(power + flowDelta * 0.6).toFixed(2);
    }

    const vibration = sensors['vibration-amplitude'];
    if (vibration != null && power != null && prevSensors['vibration-amplitude'] != null) {
      const vibDelta = vibration - prevSensors['vibration-amplitude'];
      sensors['elec-power'] = +(power + vibDelta * 0.4).toFixed(2);
    }

    const oilTemp = sensors['oil-temp'];
    const oilLevel = sensors['oil-level'];
    if (oilTemp != null && oilLevel != null && prevSensors['oil-temp'] != null) {
      const tempDelta = oilTemp - prevSensors['oil-temp'];
      sensors['oil-level'] = +(oilLevel - tempDelta * 0.2).toFixed(2);
    }
  }

  // Electrical equipment correlations
  if (category.includes('ELECTRICAL') || equipType.includes('transformer') || equipType.includes('distribution')) {
    const voltage = sensors['voltage'];
    const current = sensors['current'];

    if (voltage != null && current != null && prevSensors['voltage'] != null) {
      const voltageDelta = voltage - prevSensors['voltage'];
      sensors['current'] = +(current + voltageDelta * 0.7).toFixed(2);
    }

    const oilTemp = sensors['oil-temp'];
    if (current != null && oilTemp != null && prevSensors['current'] != null) {
      const currentDelta = current - prevSensors['current'];
      sensors['oil-temp'] = +(oilTemp + currentDelta * 0.5).toFixed(2);
    }
  }

  // General building sensors correlations
  const temp = sensors['space-temp'] || sensors['zone-temp'];
  const humidity = sensors['space-humidity'] || sensors['zone-humidity'];
  const co2 = sensors['co2'];
  const voc = sensors['voc'];
  const pm25 = sensors['pm2.5'];

  if (temp != null && prevSensors['space-temp'] != null) {
    const tempDelta = temp - prevSensors['space-temp'];

    if (humidity != null) {
      const humidityKey = sensors['space-humidity'] != null ? 'space-humidity' : 'zone-humidity';
      sensors[humidityKey] = +(humidity - tempDelta * 0.2).toFixed(2);
    }

    if (co2 != null) {
      sensors['co2'] = +(co2 + tempDelta * 0.3).toFixed(2);
    }
  }

  if (humidity != null && (prevSensors['space-humidity'] != null || prevSensors['zone-humidity'] != null)) {
    const prevHumidity = prevSensors['space-humidity'] || prevSensors['zone-humidity'];
    const humidityDelta = humidity - prevHumidity;

    if (temp != null) {
      const tempKey = sensors['space-temp'] != null ? 'space-temp' : 'zone-temp';
      sensors[tempKey] = +(temp - humidityDelta * 0.2).toFixed(2);
    }

    if (pm25 != null) {
      sensors['pm2.5'] = +(pm25 - humidityDelta * 0.2).toFixed(2);
    }
  }

  if (co2 != null && voc != null && prevSensors['co2'] != null) {
    const co2Delta = co2 - prevSensors['co2'];
    sensors['voc'] = +(voc + co2Delta * 0.4).toFixed(2);
  }

  return sensors;
}

// ============================================================================
// CONTROL LOGIC
// ============================================================================

function applyControlLogic(equipment, sensors, load) {
  let powerMultiplier = 1.0;
  const category = equipment.category.toUpperCase();

  if (equipment.controlRules && equipment.controlRules.length > 0) {
    for (const rule of equipment.controlRules) {
      if (!rule.enabled) continue;

      const condSensor = sensors[rule.condition.sensor];
      if (condSensor == null) continue;

      let conditionMet = false;
      const { operator, value } = rule.condition;

      switch (operator) {
        case '>': conditionMet = condSensor > value; break;
        case '<': conditionMet = condSensor < value; break;
        case '>=': conditionMet = condSensor >= value; break;
        case '<=': conditionMet = condSensor <= value; break;
        case '==': conditionMet = condSensor === value; break;
        case '!=': conditionMet = condSensor !== value; break;
      }

      if (conditionMet) {
        for (const action of rule.actions) {
          const targetSensor = sensors[action.sensor];
          if (targetSensor == null) continue;

          switch (action.operation) {
            case 'multiply':
              sensors[action.sensor] = targetSensor * action.value;
              break;
            case 'add':
              sensors[action.sensor] = targetSensor + action.value;
              break;
            case 'set':
              sensors[action.sensor] = action.value;
              break;
          }
        }
      }
    }
  } else {
    const equipType = equipment.equipmentTypeName.toLowerCase();

    // Chiller control logic
    if (category.includes('CHILLER') || equipType.includes('chiller')) {
      const lwt = sensors['chilled-water-leaving-temp'];
      const power = sensors['elec-power'];
      const returnTemp = sensors['return-air-temp'];

      if (load > 0.6 && lwt != null && power != null && lwt > 50) {
        sensors['elec-power'] = +(power * 1.05).toFixed(2);
        const flow = sensors['chilled-water-flow'];
        if (flow != null) {
          sensors['chilled-water-flow'] = +(flow * 1.03).toFixed(2);
        }
      }

      if (returnTemp != null && returnTemp > 26 && power != null) {
        powerMultiplier *= 1.25;
        const evapPressure = sensors['evaporator-pressure'] || 300;
        sensors['evaporator-pressure'] = +(evapPressure * 1.1).toFixed(2);
      }
    }

    // AHU control logic
    if (category.includes('AHU') || category.includes('AIR') || equipType.includes('air handling')) {
      const supplyTemp = sensors['discharge-air-temp'];
      const power = sensors['elec-power'];
      const co2Level = sensors['co2'];

      if (supplyTemp != null && power != null && supplyTemp > 95) {
        sensors['elec-power'] = +(power * 1.05).toFixed(2);
        const airflow = sensors['discharge-air-flow'];
        if (airflow != null) {
          sensors['discharge-air-flow'] = +(airflow * 1.05).toFixed(2);
        }
      }

      const humidity = sensors['discharge-air-humidity'];
      const airflow = sensors['discharge-air-flow'];

      if (humidity != null && airflow != null && humidity > 60) {
        sensors['discharge-air-flow'] = +(airflow * 1.05).toFixed(2);
      }

      if (co2Level != null && co2Level > 800 && airflow != null && power != null) {
        sensors['discharge-air-flow'] = +(airflow * 1.2).toFixed(2);
        powerMultiplier *= 1.15;
      }
    }

    // Boiler control logic
    if (equipType.includes('boiler')) {
      const outletTemp = sensors['water-outlet-temp'];
      const fuelFlow = sensors['fuel-flow'];
      const steamPressure = sensors['steam-pressure'];
      const power = sensors['elec-power'];

      if (outletTemp != null && outletTemp < 85) {
        if (fuelFlow != null) {
          sensors['fuel-flow'] = +(fuelFlow * 1.2).toFixed(2);
        }
        if (steamPressure != null) {
          sensors['steam-pressure'] = +(steamPressure * 1.1).toFixed(2);
        }
        if (power != null) {
          powerMultiplier *= 1.1;
        }
      }
    }

    // Fan Coil control logic
    if (equipType.includes('fan coil')) {
      const roomTemp = sensors['space-temp'] || sensors['zone-temp'];
      const airflow = sensors['discharge-air-flow'] || sensors['air-flow'];
      const power = sensors['elec-power'];

      if (roomTemp != null && roomTemp > 24 && airflow != null && power != null) {
        sensors[sensors['discharge-air-flow'] != null ? 'discharge-air-flow' : 'air-flow'] = +(airflow * 1.1).toFixed(2);
        powerMultiplier *= 1.1;
      }
    }

    // Pump control logic
    if (equipType.includes('pump')) {
      const flowRate = sensors['water-flow'] || sensors['flow-rate'];
      const typicalFlow = sensors['typical-flow'] || 1;
      const power = sensors['elec-power'];

      if (flowRate != null && flowRate < 0.8 * typicalFlow && power != null) {
        const flowKey = sensors['water-flow'] != null ? 'water-flow' : 'flow-rate';
        sensors[flowKey] = +(flowRate * 1.2).toFixed(2);
        powerMultiplier *= 1.05;
      }
    }
  }

  return { sensors, powerMultiplier };
}

// ============================================================================
// ANOMALY INJECTION
// ============================================================================

function maybeInjectAnomaly(equipment, sensors, timestamp) {
  const anomalyConfig = equipment.anomalyConfig;
  if (!anomalyConfig || !anomalyConfig.enabled) {
    return false;
  }

  let shouldInject = false;

  if (anomalyConfig.mode === 'probability') {
    const probability = anomalyConfig.probability || 0.01;
    shouldInject = Math.random() < probability;
  } else if (anomalyConfig.mode === 'schedule') {
    const count = anomalyConfig.count || 0;
    const period = anomalyConfig.period || 'day';
    const periodHours = period === 'day' ? 24 : period === 'week' ? 168 : 720;
    const probability = count / periodHours;
    shouldInject = Math.random() < probability;
  }

  if (!shouldInject) {
    return false;
  }

  const dataPoints = equipment.dataPoints.filter(dp => dp.type === 'float');
  if (dataPoints.length === 0) {
    return false;
  }

  const dp = dataPoints[Math.floor(Math.random() * dataPoints.length)];
  const haystackTag = dp.haystackTag;
  const val = sensors[haystackTag];

  if (val == null) {
    return false;
  }

  const mode = Math.random();
  let newVal = val;

  if (mode < 0.33) {
    newVal = val * (1.5 + Math.random());
  } else if (mode < 0.66) {
    newVal = val * (0.1 + 0.2 * Math.random());
  } else {
    newVal = val + randBetween(-10, 10);
  }

  sensors[haystackTag] = +newVal.toFixed(2);
  return true;
}

// ============================================================================
// EQUIPMENT SIMULATION
// ============================================================================

function simulateEquipmentInstance(equipment, timestamp, stepHours, displayRanges, state) {
  const date = new Date(timestamp);
  const load = getEnvLoad(date);
  const equipmentId = equipment.id;

  if (!state[equipmentId]) {
    state[equipmentId] = {
      lastSensors: {},
      cumulativeEnergy: 0,
      runtimeHours: 0,
      dailyStarts: 0,
      lastDay: undefined
    };
  }

  const equipState = state[equipmentId];
  const prevSensors = equipState.lastSensors;

  const { powerProfile } = equipment;
  const mode = Math.random() < 0.33 ? 'low' : Math.random() < 0.66 ? 'medium' : 'high';

  let powerDraw = powerProfile[mode] * powerProfile.powerLoadFactor * load;

  const sensors = {};

  for (const dp of equipment.dataPoints) {
    const haystackTag = dp.haystackTag;

    if (dp.type === 'boolean') {
      const prev = prevSensors[haystackTag];

      // 1. If first run → use typical or default true
      if (prev == null) {
        sensors[haystackTag] = dp.typical ?? true;
      }
      // 2. Small probability of toggle (faults, trips, cycles)
      else if (Math.random() < 0.01) {
        sensors[haystackTag] = !prev;
      }
      // 3. Otherwise retain state
      else {
        sensors[haystackTag] = prev;
      }

      continue;
    }

    const range = getRange(displayRanges, haystackTag, { normalMin: dp.min, normalMax: dp.max });

    let value;

    if (prevSensors[haystackTag] != null) {
      const drift = (range.normalMax - range.normalMin) * randBetween(-0.02, 0.02);
      value = prevSensors[haystackTag] + drift;
      value = clamp(value, range.normalMin, range.normalMax);
    } else {
      value = valueInNormalBand(range.normalMin, range.normalMax, load, 0.1);
    }

    value = maybeOutlier(value, range.normalMin, range.normalMax);

    sensors[haystackTag] = +value.toFixed(2);
  }

  if (sensors['elec-power'] != null) {
    sensors['elec-power'] = +powerDraw.toFixed(2);
  }

  const controlled = applyControlLogic(equipment, sensors, load);

  powerDraw *= controlled.powerMultiplier;

  const nowDate = new Date(timestamp);
  const hour = nowDate.getHours();
  const month = nowDate.getMonth();
  const timeFactor = getTimeOfDayFactor(hour);
  const occFactor = getOccupancyFactor(hour);
  const monthEquipName = equipment.equipmentTypeName || equipment.equipmentTypeCode;
  const monthFactors = MONTH_EQUIPMENT_FACTORS[monthEquipName] || [];
  const monthEquipFactor = monthFactors[month] ?? 1;
  powerDraw *= timeFactor * occFactor * monthEquipFactor;

  if (sensors['elec-power'] != null) {
    sensors['elec-power'] = +powerDraw.toFixed(2);
  }

  const configCorrelations = equipment.sensorCorrelations && equipment.sensorCorrelations.length > 0
    ? equipment.sensorCorrelations
    : [];
  applySensorCorrelations(equipment, sensors, prevSensors, configCorrelations);

  const anomalyInjected = maybeInjectAnomaly(equipment, sensors, timestamp);

  equipState.lastSensors = { ...sensors };
  const stepEnergy = powerDraw * stepHours;
  equipState.cumulativeEnergy += stepEnergy;

  return {
    equipmentId,
    equipmentType: equipment.equipmentTypeName,
    category: equipment.category,
    powerDraw: +powerDraw.toFixed(2),
    stepEnergy: +stepEnergy.toFixed(4),
    sensors,
    anomalyInjected
  };
}

// ============================================================================
// EQUIPMENT INTERACTIONS
// ============================================================================

function applyEquipmentInteractions(scenario, results) {
  if (!scenario.equipmentInteractions || scenario.equipmentInteractions.length === 0) {
    return;
  }

  for (const interaction of scenario.equipmentInteractions) {
    const fromEquip = results.find(r => r.equipmentType === interaction.fromEquipmentType);
    const toEquip = results.find(r => r.equipmentType === interaction.toEquipmentType);

    if (!fromEquip || !toEquip) continue;

    for (const link of interaction.sensorLinks) {
      const fromValue = fromEquip.sensors[link.fromSensor];
      const toValue = toEquip.sensors[link.toSensor];

      if (fromValue == null || toValue == null) continue;

      const delta = fromValue * link.factor;
      toEquip.sensors[link.toSensor] = +(toValue + delta).toFixed(2);
    }
  }
}

// ============================================================================
// TELEMETRY HELPERS
// ============================================================================

async function findTelemetryCollectionsByAssetType(ctx, IafItemSvc, assetTypes) {
  const wanted = new Set(assetTypes.map((s) => s.toLowerCase()));
  const result = {};
  try {
    const colls = await IafItemSvc.getNamedUserItems(
      { query: { _userType: TELEMETRY_COLL_USER_TYPE, _kind: 'collection' } },
      ctx
    );
    for (const c of colls._list || []) {
      const tip = c._versions?.find((v) => v._isTip);
      const attrs = (tip?._userAttributes || {});
      const assetType = (attrs['Asset Type'] || '').toString();
      if (!assetType || !wanted.has(assetType.toLowerCase())) continue;

      const tsProps = (attrs['_tsProperties'] || c?._tsProperties || {});
      const gran = (tsProps?._granularity || '').toString().toLowerCase();

      if (!result[assetType]) result[assetType] = {};
      if (gran === 'hours' || gran === 'hour' || gran === 'hourly' || attrs['aggregation'] == "hourly") {
        result[assetType].hourly = c;
      } else {
        result[assetType].raw = c;
      }
    }
  } catch (e) {
    console.warn('findTelemetryCollectionsByAssetType failed:', e);
  }
  return result;
}

async function fetchLatestTimestampAcrossCollections(ctx, IafItemSvc, collections) {
  let latest = null;
  for (const asset of Object.keys(collections)) {
    const raw = collections[asset]?.raw;
    if (!raw) continue;
    const pipeline = [
      { $sort: { _ts: -1 } },
      { $limit: 1 },
      { $project: { _ts: 1 } },
    ];
    try {
      const res = await IafItemSvc.aggregateReadings(raw._id, pipeline, ctx);
      const row = res?._list?.[0];
      const ts = row?._ts;
      if (ts && (!latest || new Date(ts).getTime() > new Date(latest).getTime())) {
        latest = ts;
      }
    } catch (e) {
      console.warn('aggregateReadings failed for collection', raw._id, e);
    }
  }
  return latest;
}

async function persistRawReadings(ctx, IafItemSvc, tickResults, scenario, dpIndex, collectionsByAsset, dryRun) {
  const rawItemsCache = {};

  async function getRawItemsForCollection(userItemId) {
    if (rawItemsCache[userItemId]) return rawItemsCache[userItemId];
    try {
      const res = await IafItemSvc.getRelatedItems(userItemId, {}, ctx, { page: { getAllItems: true } });
      const list = (res._list || []);
      rawItemsCache[userItemId] = list;
      return list;
    } catch (e) {
      console.warn('persistRawReadings: failed to load raw telemetry items for', userItemId, e);
      rawItemsCache[userItemId] = [];
      return [];
    }
  }

  async function resolveTelemetryItem(assetType, equipmentId) {
    const rawColl = collectionsByAsset[assetType]?.raw;
    if (!rawColl || !rawColl._userItemId) return {};
    const items = await getRawItemsForCollection(rawColl._userItemId);
    const bySource = items.find((it) => (it._sourceId || '').toString() === equipmentId);
    return { rawCollection: rawColl, telemetryItem: bySource || items[0] };
  }

  const readings = [];
  const batches = {};

  for (const tick of tickResults) {
    const ts = tick.timestamp;
    for (const eq of tick.equipment) {
      const assetType = eq.equipmentType;
      const { rawCollection, telemetryItem } = await resolveTelemetryItem(assetType, eq.equipmentId);
      if (!rawCollection || !rawCollection._userItemId || !telemetryItem) {
        console.warn('persistRawReadings: missing collection or telemetry item for', assetType, eq.equipmentId);
        continue;
      }

      const equipCfg = scenario.equipment.find((e) => e.id === eq.equipmentId);
      const dtType = (equipCfg?.equipmentTypeName || '').toLowerCase();
      const sensorsMapped = {};
      for (const [hay, val] of Object.entries(eq.sensors)) {
        const idxKey = `${dtType}::${hay}`.toLowerCase();
        const dp = dpIndex[idxKey];
        const field = (dp?.pointName || dp?.name || hay);
        sensorsMapped[field] = val;
      }

      const rawReading = {
        _ts: ts,
        ...sensorsMapped,
        _tsMetadata: {
          _telItemId: telemetryItem._id,
          _sourceId: telemetryItem._sourceId || telemetryItem._id,
        },
      };

      readings.push(rawReading);

      const key = rawCollection._userItemId;
      if (!batches[key]) batches[key] = [];
      batches[key].push(rawReading);
    }
  }

  if (dryRun) {
    return { readings, insertedCount: 0 };
  }

  let inserted = 0;
  for (const [userItemId, docs] of Object.entries(batches)) {
    try {
      const res = await IafItemSvc.createRelatedItemsBulk(userItemId, docs, ctx);
      inserted += res?.length || 0;
    } catch (e) {
      console.warn('persistRawReadings failed for collection userItemId', userItemId, e);
    }
  }

  return { readings, insertedCount: inserted };
}

// ============================================================================
// MAIN SIMULATION ENGINE
// ============================================================================

/**
 * Run unified simulation
 *
 * @param {Object} options
 * @param {string} options.scenarioId - Optional scenario ID to load from collection
 * @param {Object} options.scenario - The simulation scenario configuration (alternative to scenarioId)
 * @param {Object} options.simPeriod - Optional { from: string, to: string }
 * @param {boolean} options.dryRun - If true, don't persist readings
 * @param {number} options.stepSeconds - Time step in seconds (default: 5)
 * @param {Object} libraries - PlatformApi
 * @param {Object} ctx - Platform context with authToken and _namespaces
 * @returns {Promise<Object>} Simulation results
 */
async function runUnifiedSimulation(options, libraries, ctx) {
  const {
    scenarioId,
    scenarioName,
    scenario: providedScenario,
    simPeriod,
    dryRun = false,
    stepSeconds = 5
  } = options;

  const {PlatformApi} = libraries;
  const {IafItemSvc} = PlatformApi

  let scenario = providedScenario;

  // Load scenario by ID if scenarioId provided and no scenario object given
  if ((scenarioId || scenarioName) && !scenario) {
    console.log('Loading scenario by ID or Name:', scenarioId, scenarioName);
    try {
      // First, find the scenario collection
      const collections = await IafItemSvc.getNamedUserItems(
        { query: { _userType: SIMULATION_SCENARIO_USER_TYPE, _kind: 'collection' } },
        ctx
      );

      if (!collections._list || collections._list.length === 0) {
        throw new Error(`No scenario collection found with userType: ${SIMULATION_SCENARIO_USER_TYPE}`);
      }

      const scenarioCollection = collections._list[0];

      // Get the scenario by ID from the collection
      const scenariosRes = await IafItemSvc.getRelatedItems(
        scenarioCollection._userItemId || scenarioCollection._id,
        { query: scenarioId ? { _id: scenarioId } : {name: scenarioName} },
        ctx
      );

      if (!scenariosRes._list || scenariosRes._list.length === 0) {
        throw new Error(`Scenario not found with ID: ${scenarioId}`);
      }

      scenario = scenariosRes._list[0];
      console.log('Loaded scenario:', scenario._name || scenario.name || scenarioId);
    } catch (e) {
      console.error('Failed to load scenario by ID:', e);
      throw new Error(`Failed to load scenario: ${e.message}`);
    }
  }

  if (!scenario) {
    throw new Error('No scenario provided. Pass either scenarioId or scenario object.');
  }

  // Sanitize any Decimal128 objects in the scenario
  scenario = sanitizeDecimal128(scenario);

  // Load display ranges and display points index
  const displayRanges = await loadDisplayRanges(ctx, IafItemSvc);
  const dpIndex = await loadDisplayPointsIndex(ctx, IafItemSvc);

  // Enrich scenario equipment datapoints
  scenario = enrichScenarioEquipmentDataPoints(scenario, dpIndex, displayRanges);

  // Determine simulation period
  const now = new Date();
  let from;
  let to;
  let collectionsByAsset = {};

  if (simPeriod?.from && simPeriod?.to) {
    from = new Date(simPeriod.from);
    to = new Date(simPeriod.to);
  } else {
    const assetTypes = Array.from(new Set((scenario.equipment || []).map((e) => e.equipmentTypeName).filter(Boolean)));
    collectionsByAsset = await findTelemetryCollectionsByAssetType(ctx, IafItemSvc, assetTypes);
    const latestTs = await fetchLatestTimestampAcrossCollections(ctx, IafItemSvc, collectionsByAsset);
    to = now;
    const baseFrom = latestTs ? new Date(new Date(latestTs).getTime() + (stepSeconds * 1000)) : new Date(now.getTime() - 60 * 60 * 1000);
    from = baseFrom;
  }

  const stepHours = stepSeconds / 3600;
  const state = {};
  const results = [];
  let dryRunRawReadings;

  let current = new Date(from);

  // Main simulation loop
  while (current <= to) {
    const timestamp = current.toISOString();
    const equipmentResults = [];

    for (const equipment of scenario.equipment) {
      const result = simulateEquipmentInstance(
        equipment,
        timestamp,
        stepHours,
        displayRanges,
        state
      );
      equipmentResults.push(result);
    }

    applyEquipmentInteractions(scenario, equipmentResults);

    const totalPower = equipmentResults.reduce((sum, r) => sum + r.powerDraw, 0);
    const stepEnergy = equipmentResults.reduce((sum, r) => sum + r.stepEnergy, 0);

    results.push({
      timestamp,
      totalPower: +totalPower.toFixed(2),
      stepEnergy: +stepEnergy.toFixed(2),
      equipment: equipmentResults
    });

    current = new Date(current.getTime() + stepSeconds * 1000);
  }

  // Persist readings
  let rawCount = 0;
  try {
    if (Object.keys(collectionsByAsset).length === 0) {
      const assetTypes = Array.from(new Set((scenario.equipment || []).map((e) => e.equipmentTypeName).filter(Boolean)));
      collectionsByAsset = await findTelemetryCollectionsByAssetType(ctx, IafItemSvc, assetTypes);
    }
    const { readings, insertedCount } = await persistRawReadings(
      ctx,
      IafItemSvc,
      results,
      scenario,
      dpIndex,
      collectionsByAsset,
      dryRun
    );

    rawCount = insertedCount;
    if (dryRun) {
      dryRunRawReadings = readings;
    }
  } catch (e) {
    console.warn('persistRawReadings failed:', e);
  }

  // Log dry run results
  if (dryRun) {
    try {
      const sample = results.slice(0, 3);
      const dryRunRawReadingsSample = dryRunRawReadings?.slice(0, 3);
      console.log('[SIM] Dry Run Results', `ticks=${results.length}`, {scenario});
      console.log('Period:', { from: from.toISOString(), to: to.toISOString(), stepSeconds });
      console.log('Sample (first 3 ticks):', sample.map(r => ({
        timestamp: r.timestamp,
        totalPower: r.totalPower,
        stepEnergy: r.stepEnergy,
        equipmentIds: (r.equipment || []).map(e => e.equipmentId)
      })));
      console.log('Sample (first 3 readings):', dryRunRawReadingsSample);
    } catch (e) {
      console.warn('[SIM] Failed to log dry run results:', e);
    }
  }

  return {
    dryRun,
    simPeriodUsed: {
      from: from.toISOString(),
      to: to.toISOString()
    },
    results,
    rawReadingsCount: rawCount,
    hourlyReadingsCount: 0,
    ...(dryRun && dryRunRawReadings ? { dryRunRawReadings } : {})
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  runUnifiedSimulation,
  loadDisplayRanges,
  loadDisplayPointsIndex,
  sanitizeDecimal128,
  DISPLAY_POINTS_USER_TYPE,
  TELEMETRY_COLL_USER_TYPE,
  SIMULATION_SCENARIO_USER_TYPE
};

